import {
  Alert,
  Anchor,
  Button,
  Group,
  Modal,
  NumberInput,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link } from 'react-router';
import { rawApi } from '../../api/client';
import type { ApiError } from '../../api/errors';
import { listKey } from '../../api/queryKeys';
import { PaperPicker, type StoredPaper } from '../../components/ThePaperItself';
import {
  FILED_MONTH_REASON_FIELD,
  isFiledMonthWarning,
  WritingIntoAFiledMonth,
} from '../../components/WritingIntoAFiledMonth';
import { useIslandToday } from '../../hooks/useSeedLocation';
import { enumLabel, enumOptions } from '../../lib/enums';
import {
  currencySymbol,
  formatMoney,
  type NumberInputValue,
  toIsoDate,
  toMinorUnits,
} from '../../lib/format';
import { notifyDone, notifyError } from '../../lib/notify';

const BILLS_PATH = '/admin/v1/suppliers/supplier-invoices';
const PAYABLES_PATH = '/admin/v1/suppliers/payables';

/** Their terms turned into the day it falls due, so nobody counts fifteen days on a calendar. */
function dueFromTerms(invoiceDate: string, termsDays: number | null | undefined): string {
  if (!invoiceDate || !termsDays) return '';
  const d = new Date(`${invoiceDate}T12:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  d.setDate(d.getDate() + termsDays);
  return toIsoDate(d) ?? '';
}

export interface BillVendor {
  id: string;
  name: string;
  invoiceCurrency?: string | null;
  paymentTermsDays?: number | null;
}

/**
 * Writing down a bill that is not a delivery.
 *
 * The gas man comes and fills the propane tank. There is no crate, no shelf and nothing to count —
 * there is a bill, from a vendor, on a date, for an amount. Recording it used to mean recording a
 * delivery, which meant inventing a stock item called "Propane" and a quantity, and then carrying
 * a number nobody counts and nobody will ever reconcile. Same for the things that DO arrive and
 * are deliberately not counted: five-gallon drinking water, ice.
 *
 * ## Why this is a second button rather than a question on the first one
 *
 * Recording a delivery and recording a bill look alike only in a data model. On the screen they
 * are not the same job: a delivery is a list of goods, quantities, an exchange rate and the shelf
 * they went onto, entered by whoever is standing at the door with the crates; this is four boxes
 * filled in off a piece of paper on the counter, days later, usually by the owner. Merging them
 * behind "Record a bill…" that then asks "was anything delivered?" would put a question in front
 * of both jobs whose answer the person already knew before they clicked, and it would land the
 * propane bill in a form built around a line editor.
 *
 * They also start from different places, which is the stronger reason. A delivery is recorded from
 * the order it came off, or from Deliveries; a bill is recorded from the Bills list on the vendor's
 * own page, beside the bills it will join and the "Record a payment" that settles them. Each button
 * sits next to the thing it produces, and neither asks the operator to describe what they are
 * doing before letting them do it.
 *
 * Nothing here moves stock, and it says so on screen: a bill with no lines contributes nothing to
 * what a drink costs, which is the whole point of not tracking the ice.
 */
export function RecordBillForm({
  vendor,
  onClose,
  onSaved,
}: {
  vendor: BillVendor;
  onClose: () => void;
  onSaved: () => void;
}) {
  const theirCurrency = vendor.invoiceCurrency ?? 'HNL';
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState<NumberInputValue>('');
  const [currency, setCurrency] = useState(theirCurrency);
  /**
   * The date the paper is dated, defaulted from the BAR's calendar rather than the browser's.
   *
   * `toIsoDate(new Date())` is the reader's local day. Roatán is UTC−6 and the office is often not on
   * the island, so this is the sixth place the same six-hour defect has been found: a bill typed at
   * half past seven on the last night of a month was dated the first of the next one, and dropped out
   * of that month's ISV return. `useIslandToday` carries the reasoning.
   */
  const today = useIslandToday();
  const [invoiceDate, setInvoiceDate] = useState(today);
  const [dueDate, setDueDate] = useState(dueFromTerms(today, vendor.paymentTermsDays));
  const [invoiceNumber, setInvoiceNumber] = useState('');
  /**
   * The photograph of the paper on the counter, uploaded the moment it is chosen.
   *
   * This form is the exact case the whole thing exists for: the gas man's bill, typed off a piece
   * of paper that will not survive the week, and a question six weeks later about whether the
   * meter reading was right.
   */
  const [paper, setPaper] = useState<StoredPaper | null>(null);
  /** True once the owner has touched the due date, so their answer is never overwritten. */
  const [dueTouched, setDueTouched] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /**
   * Set when the bill turned out to be dated in a month the accountant has already filed.
   *
   * This form is exactly the case the warning was written about: the gas man's bill that arrived a
   * fortnight late, dated the twelfth, typed on the third of the following month. The dialog asks
   * why and sends the same bill again — the form stays open behind it, so cancelling returns the
   * owner to what he typed rather than losing it.
   */
  const [filedMonthWarning, setFiledMonthWarning] = useState<ApiError | null>(null);

  const amountMinor = toMinorUnits(amount) ?? 0;

  const save = useMutation({
    mutationFn: (input: { filedMonthReason?: string } = {}) =>
      rawApi('POST', BILLS_PATH, {
        supplierId: vendor.id,
        description: description.trim(),
        totalMinor: amountMinor,
        currency,
        invoiceDate,
        // Cleared on purpose means due on delivery, and is sent as such: leaving it out would have
        // the server work one out from their terms, which is the opposite of what was just said.
        dueDate: dueDate || null,
        invoiceNumber: invoiceNumber.trim() || null,
        sourceDocumentId: paper?.id ?? null,
        // Attached only once the server has asked. A box on every bill is a box people learn to
        // fill with "n/a", including on the one bill where the answer matters.
        ...(input.filedMonthReason ? { [FILED_MONTH_REASON_FIELD]: input.filedMonthReason } : {}),
      }),
    onSuccess: () => {
      setFiledMonthWarning(null);
      onSaved();
      onClose();
      notifyDone(
        'Bill recorded',
        `${formatMoney(amountMinor, currency)} owed to ${vendor.name}. It is on the list of what you owe until it is paid.`,
      );
    },
    onError: (err: Error) => {
      // A filed month is a question, not a refusal: the paper is real and has to be recorded.
      if (isFiledMonthWarning(err)) return setFiledMonthWarning(err);
      notifyError(err, 'record this bill');
    },
  });

  const submit = () => {
    if (!description.trim()) return setError('Say what the bill was for');
    if (amountMinor <= 0) return setError('Say how much they billed you');
    if (!invoiceDate) return setError('Say what date is on the bill');
    setError(null);
    save.mutate({});
  };

  return (
    <Modal
      opened
      onClose={onClose}
      title={`Record a bill from ${vendor.name}`}
      size="lg"
      // Half-completed forms are lost to a stray click otherwise.
      closeOnClickOutside={false}
    >
      <Stack gap="md">
        {/* The way out, not just the warning. This is the easy door to a bill and the delivery
            door is on another screen, so a manager holding an invoice for a crate of limes finds
            this form first — and a bill recorded here puts nothing on a shelf and leaves the limes
            out of what a drink costs. Telling him he is in the wrong place without saying where
            the right one is leaves him to either go looking or carry on. */}
        <Text size="sm" c="dimmed">
          For a bill with nothing to count behind it — the propane, the ice, the drinking water. Nothing goes
          onto a shelf and nothing changes what a drink costs. If goods arrived that you do count,{' '}
          <Anchor component={Link} to="/suppliers?tab=deliveries" size="sm">
            record the delivery instead
          </Anchor>
          .
        </Text>

        <TextInput
          label="What it was for"
          description="In the words on the bill, so this row can be checked against the paper later"
          placeholder="Propane refill"
          required
          value={description}
          onChange={(e) => setDescription(e.currentTarget.value)}
          disabled={save.isPending}
        />

        <Group grow align="flex-start">
          <NumberInput
            label="How much they billed you"
            required
            min={0}
            prefix={`${currencySymbol(currency)} `}
            decimalScale={2}
            fixedDecimalScale
            thousandSeparator=","
            hideControls
            value={amount}
            // Kept exactly as the box hands it back — see `NumberInputValue` in lib/format.
            onChange={setAmount}
            disabled={save.isPending}
          />
          <Select
            label="They billed you in"
            description={`${vendor.name} normally bills in ${enumLabel('currency', theirCurrency)}`}
            required
            data={enumOptions('currency')}
            value={currency}
            onChange={(v) => setCurrency(v ?? theirCurrency)}
            allowDeselect={false}
            comboboxProps={{ withinPortal: true }}
            disabled={save.isPending}
          />
        </Group>

        <Group grow align="flex-start">
          <DateInput
            label="Dated"
            description="The date on the bill, not the day you are typing it"
            required
            value={invoiceDate || null}
            onChange={(v) => {
              const next = v ?? '';
              setInvoiceDate(next);
              if (!dueTouched) setDueDate(dueFromTerms(next, vendor.paymentTermsDays));
            }}
            valueFormat="D MMM YYYY"
            popoverProps={{ withinPortal: true }}
            disabled={save.isPending}
          />
          <DateInput
            label="To be paid by"
            description={
              vendor.paymentTermsDays
                ? `Worked out from their ${vendor.paymentTermsDays} days`
                : 'Leave blank if you pay on the spot'
            }
            value={dueDate || null}
            onChange={(v) => {
              setDueTouched(true);
              setDueDate(v ?? '');
            }}
            valueFormat="D MMM YYYY"
            clearable
            popoverProps={{ withinPortal: true }}
            disabled={save.isPending}
          />
          <TextInput
            label="Invoice number"
            description="If they gave you one"
            value={invoiceNumber}
            onChange={(e) => setInvoiceNumber(e.currentTarget.value)}
            disabled={save.isPending}
          />
        </Group>

        <PaperPicker value={paper} onChange={setPaper} disabled={save.isPending} />

        {/* Lempiras and dollars are twenty-six times apart and both are everyday money here. A bill
            entered in the wrong one is not found until the vendor's own statement disagrees. */}
        {currency !== theirCurrency && (
          <Alert color="yellow" variant="light" title="That is not the money they usually bill in">
            {vendor.name} normally bills in {enumLabel('currency', theirCurrency)}, and this is being recorded
            in {enumLabel('currency', currency)}. It can only be paid in {enumLabel('currency', currency)}{' '}
            afterwards.
          </Alert>
        )}

        {error && (
          <Text size="sm" c="red">
            {error}
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={save.isPending}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            Record the bill
          </Button>
        </Group>
      </Stack>
      <WritingIntoAFiledMonth
        warning={filedMonthWarning}
        saving={save.isPending}
        onCancel={() => setFiledMonthWarning(null)}
        onConfirm={(filedMonthReason) => save.mutate({ filedMonthReason })}
      />
    </Modal>
  );
}

/**
 * The button that starts a bill from the vendor you are already looking at.
 *
 * Beside the Bills heading rather than inside the list, so it is there on the day a vendor has no
 * bills at all — which is the day somebody most needs to record one. The vendor is already known,
 * so nothing on this path asks the owner to pick Zeta Gas out of a dropdown from Zeta Gas's page.
 */
export function RecordBillButton({ vendor }: { vendor: BillVendor }) {
  const queryClient = useQueryClient();
  const [recording, setRecording] = useState(false);
  return (
    <>
      <Button size="compact-sm" onClick={() => setRecording(true)}>
        Record a bill
      </Button>
      {recording && (
        <RecordBillForm
          vendor={vendor}
          onClose={() => setRecording(false)}
          onSaved={() => {
            // This page's own bill list, and the Bills-to-pay tab that shares the payables cache.
            void queryClient.invalidateQueries({
              queryKey: listKey(BILLS_PATH, { supplierId: vendor.id }),
            });
            void queryClient.invalidateQueries({ queryKey: listKey(PAYABLES_PATH) });
          }}
        />
      )}
    </>
  );
}
